import { isFunction, isNumber, noop } from 'underscore';
import { createMachine, assign, spawn, send } from 'xstate';
import { logAction } from './debug';
import { StepEvents } from './step-events';
import { WizardEvents } from './wizard-events';

const WizardStates = {
  INIT: 'INIT',
  STEP_ACTIVE: 'STEP_ACTIVE',
  STEP_LOADING: 'STEP_LOADING',
  STEP_SAVING: 'STEP_SAVING',
  STEP_FAILED: 'STEP_FAILED',
  STEP_CANCELING: 'STEP_CANCELING',
  DONE: 'DONE',
  FIN: 'FIN',
};

// Public state selectors
export const stepListSelector = state => state.context.stepList;
export const stepIndexSelector = state => state.context.stepIndex;
export const stepServicesCacheSelector = state => state.context.stepServicesCache;
export const isBusySelector = state => state.matches(WizardStates.STEP_LOADING) || state.matches(WizardStates.STEP_SAVING) || state.matches(WizardStates.STEP_CANCELING) || state.matches(WizardStates.DONE) || state.matches(WizardStates.FIN);
export const wasSavingSelector = state => state.matches(WizardStates.STEP_SAVING);
export const wasWaitingForConfirmationSelector = state => state.matches(WizardStates.STEP_CANCELING);
export const wasStoppingSelector = state => state.matches(WizardStates.DONE) || state.matches(WizardStates.FIN);


// Derived state accessors
const getStepInfo = context => context.stepList[context.stepIndex];
const getStepServiceRef = context => context.stepServicesCache[getStepInfo(context).id];

// Guards
const doesServiceNeedActivation = context => context.stepServiceNeedsActivation;
const isNotNavigating = context => !isNumber(context.navigatingTo) && !context.isExiting;
const isExiting = context => context.isExiting || (isNumber(context.navigatingTo) && context.navigatingTo >= context.stepList.length);
const isFirstStep = context => context.stepIndex - 1 < 0;
const canNavigateBackWithoutSaving = context => getStepInfo(context).canNavigateBackWithoutSaving;

// Actions
const acquireStepService = assign((context) => {
  const stepInfo = getStepInfo(context);
  let stepService = getStepServiceRef(context);
  if (stepService) {
    return {
      ...context,
      stepService,
      stepServiceNeedsActivation: true,
    };
  }

  const extendedStepMachine = stepInfo
    .StepMachine(stepInfo.StepMachineOptions(stepInfo, context.stepsData));
  stepService = spawn(extendedStepMachine);

  return {
    ...context,
    stepServicesCache: {
      ...context.stepServicesCache,
      [stepInfo.id]: stepService,
    },
    stepService,
    stepServiceNeedsActivation: false,
  };
});

const stopStep = send(StepEvents.STOP, { to: getStepServiceRef });

const activateStep = send(
  context => ({
    type: StepEvents.ACTIVATE,
    stepsData: context.stepsData,
  }),
  { to: getStepServiceRef }
);
const deactivateStep = send(StepEvents.DEACTIVATE, { to: getStepServiceRef });
const notifyStepToSave = send(StepEvents.SAVE, { to: getStepServiceRef });
const notifyStepToCancel = send(StepEvents.CANCEL, { to: getStepServiceRef });
const setNavigatingForward = assign({
  navigatingTo: context => context.stepIndex + 1,
});
const setNavigatingBack = assign({
  navigatingTo: context => context.stepIndex - 1,
});
const setExiting = assign({
  isExiting: () => true,
});
const applyNavigation = assign({
  stepIndex: context => context.navigatingTo,
  navigatingTo: () => null,
});
const resetNavigation = assign({
  navigatingTo: () => null,
  isExiting: () => false,
});
const notifyDone = (context) => {
  if (isFunction(context.onDone)) {
    context.onDone(context.stepsData);
  }
};
const patchStepsData = assign({
  stepsData: (context, event) => ({
    ...context.stepsData,
    ...event.stepsData,
  }),
});

const defaultActions = {
  acquireStepService,
  stopStep,
  activateStep,
  deactivateStep,
  notifyStepToSave,
  notifyStepToCancel,
  setNavigatingForward,
  setNavigatingBack,
  setExiting,
  applyNavigation,
  resetNavigation,
  notifyDone,
  patchStepsData,
};

const defaultServices = {
};

const defaultGuards = {
  isFirstStep,
  isNotNavigating,
  isExiting,
  doesServiceNeedActivation,
  canNavigateBackWithoutSaving,
};

const getStartingState = (machineId, wizardOptions) => {
  const {
    stepList, stepsData, onDone = noop,
  } = wizardOptions;
  const stepIndex = 0;
  return {
    id: machineId,
    stepList,
    stepIndex,
    stepServicesCache: {},

    stepsData,

    navigatingTo: null, // pending navigation direction
    isExiting: false, // pending exit

    onDone,
  };
};

/**
 * Creates a new instance of the wizard state machine.
 * The xstate-based wizard state machine must be created only once per the lifetime of the hosting React component.
 * If props' changes need to be reflected in the state machine, the state machine must provide
 * event-based API to communicate such changes.
 * @param {object} options - wizard options
 * @returns {object} xstate machine
 */
export const wizardStateMachine = ({
  machineId = 'WizardStateMachine',
  wizardOptions,
  actions = defaultActions,
  services = defaultServices,
  guards = defaultGuards,
} = {}) => createMachine({
  preserveActionOrder: true,
  predictableActionArguments: true,
  strict: true,

  id: machineId,
  context: getStartingState(machineId, wizardOptions),
  initial: WizardStates.INIT,
  states: {
    [WizardStates.INIT]: {
      entry: ['acquireStepService'],
      always: [
        {
          target: WizardStates.STEP_ACTIVE,
          cond: 'doesServiceNeedActivation',
          actions: 'activateStep',
        },
        {
          target: WizardStates.STEP_ACTIVE,
        },
      ],
    },
    [WizardStates.STEP_ACTIVE]: {
      on: {
        [WizardEvents.STEP_LOADING]: [WizardStates.STEP_LOADING],
        [WizardEvents.CANCEL]: [
          {
            target: WizardStates.STEP_CANCELING,
            actions: ['setExiting', 'notifyStepToCancel'],
          },
        ],
        [WizardEvents.EXIT]: [
          {
            target: WizardStates.STEP_SAVING,
            actions: ['setExiting', 'notifyStepToSave'],
          },
        ],
        [WizardEvents.NAVIGATE_NEXT]: [
          {
            target: WizardStates.STEP_SAVING,
            actions: ['setNavigatingForward', 'notifyStepToSave'],
          },
        ],
        [WizardEvents.NAVIGATE_BACK]: [
          {
            // Gracefully handling of navigating back at 1st step
            cond: 'isFirstStep',
          },
          {
            target: WizardStates.INIT,
            cond: 'canNavigateBackWithoutSaving',
            actions: ['deactivateStep', 'setNavigatingBack', 'applyNavigation'],
          },
          {
            target: WizardStates.STEP_SAVING,
            actions: ['setNavigatingBack', 'notifyStepToSave'],
          },
        ],
      },
    },
    [WizardStates.STEP_LOADING]: {
      on: {
        [WizardEvents.STEP_DONE_LOADING]: [
          {
            target: WizardStates.STEP_ACTIVE,
          },
        ],
        [WizardEvents.STEP_FAILED_LOADING]: [
          {
            target: WizardStates.STEP_FAILED,
          },
        ],
      },
    },
    [WizardStates.STEP_SAVING]: {
      on: {
        [WizardEvents.STEP_DONE_SAVING]: [
          // Save and stay on the same step
          {
            target: WizardStates.STEP_ACTIVE,
            cond: 'isNotNavigating',
            actions: [logAction('STEP_SAVING: STEP_DONE_SAVING -> STEP_ACTIVE'), 'patchStepsData'],
          },
          // Save and exit
          {
            target: WizardStates.DONE,
            cond: 'isExiting',
            actions: [logAction('STEP_SAVING: STEP_DONE_SAVING -> done'), 'patchStepsData'],
          },
          // Save and navigate
          {
            target: WizardStates.INIT,
            actions: [logAction('STEP_SAVING: STEP_DONE_SAVING -> INIT'), 'patchStepsData', 'deactivateStep', 'applyNavigation'],
          },
        ],
        [WizardEvents.STEP_ABORTED_SAVING]: [
          {
            target: WizardStates.STEP_ACTIVE,
            actions: [logAction('STEP_SAVING: STEP_ABORTED_SAVING -> STEP_ACTIVE'), 'resetNavigation'],
          },
        ],
      },
    },
    [WizardStates.STEP_CANCELING]: {
      on: {
        [WizardEvents.STEP_SAVING]: [WizardStates.STEP_SAVING],
        [WizardEvents.STEP_ABORTED_CANCELING]: [
          {
            target: WizardStates.STEP_ACTIVE,
            actions: ['resetNavigation'],
          },
        ],
        [WizardEvents.STEP_DONE_CANCELING]: [WizardStates.DONE],
      },
    },
    [WizardStates.STEP_FAILED]: {},

    [WizardStates.DONE]: {
      entry: ['stopStep'],
      on: {
        [WizardEvents.STEP_STOPPED]: [
          { target: WizardStates.FIN, actions: [logAction('DONE: STEP_STOPPED -> FIN')] },
        ],
      },
    },

    [WizardStates.FIN]: { entry: [logAction('calling notifyDone'), 'notifyDone'] },
  },
}, {
  actions: {
    ...defaultActions,
    ...actions,
  },
  services: {
    ...defaultServices,
    ...services,
  },
  guards: {
    ...defaultGuards,
    ...guards,
  },
});
